/**
 * Internationalisierung der Oberflaeche.
 *
 * Bewusst eine kleine eigene Loesung statt einer Bibliothek. Der Umfang ist
 * eine Anwendung, zwei Sprachen und ein fester Schluesselbestand; die
 * Fahigkeiten einer Bibliothek — Namensraeume, Nachladen, Pluralregeln,
 * Sprachverhandlung, Interpolationsformate — werden hier nicht gebraucht.
 * Was gebraucht wird, steht in dieser Datei und ist vollstaendig lesbar.
 *
 * Der wichtigste Gewinn dieser Variante: der Schluesselbestand ist ein
 * TypeScript-Typ. Ein falscher Schluessel und eine fehlende englische
 * Uebersetzung sind Fehler beim Bauen, nicht ein leerer Text im Betrieb.
 *
 * Die Sprachwahl liegt im Browser (localStorage), nicht am Benutzerkonto.
 * Begruendung: Die Anmeldeseite braucht die Sprache, bevor es eine Sitzung
 * gibt. Eine kontogebundene Praeferenz koennte diesen Fall gar nicht bedienen
 * und verlangte zusaetzlich Schemaaenderung und Endpunkt. Fuer diesen Block
 * ist der Browser die einfachere UND die funktional bessere Ablage.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { de, type TranslationKey, type Translations } from "./de.ts";
import { en } from "./en.ts";

export const LANGUAGES = ["de", "en"] as const;
export type Language = (typeof LANGUAGES)[number];

/** Deutsch ist die Standardsprache (Auftrag: Sprachen V1). */
export const DEFAULT_LANGUAGE: Language = "de";

const DICTIONARIES: Record<Language, Translations> = { de, en };
const STORAGE_KEY = "homebase.language";

function isLanguage(value: unknown): value is Language {
  return typeof value === "string" && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * Gespeicherte Sprache lesen.
 *
 * Faellt still auf Deutsch zurueck, wenn der Speicher nicht verfuegbar ist --
 * privates Fenster, gesperrte Website-Daten. Eine nicht lesbare Praeferenz
 * darf die Anwendung nicht daran hindern zu starten.
 */
function readStored(): Language {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (isLanguage(raw)) return raw;
  } catch { /* Speicher nicht verfuegbar */ }
  return DEFAULT_LANGUAGE;
}

/** Werte fuer Platzhalter der Form {name}. */
export type Vars = Record<string, string | number>;

function interpolate(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole);
}

/**
 * Uebersetzt einen Schluessel.
 *
 * Zwei Rueckfallstufen, damit niemals ein leerer Text entsteht:
 *   1. fehlt der Text in der gewaehlten Sprache -> Deutsch
 *   2. fehlt er auch dort -> der Schluessel selbst, sichtbar und auffaellig
 *
 * Stufe 1 kann durch den Typ eigentlich nicht eintreten. Sie steht trotzdem
 * hier, weil Codes aus der Datenbank zur Laufzeit einen Schluessel bilden
 * koennen, den es noch nicht gibt.
 */
export function translate(lang: Language, key: string, vars?: Vars): string {
  const dict = DICTIONARIES[lang] as Record<string, string | undefined>;
  const text = dict[key] ?? (de as Record<string, string | undefined>)[key];
  if (text === undefined || text === "") return `⟨${key}⟩`;
  return interpolate(text, vars);
}

export interface I18n {
  readonly lang: Language;
  readonly setLang: (l: Language) => void;
  /** Uebersetzt einen bekannten Schluessel — typgeprueft. */
  readonly t: (key: TranslationKey, vars?: Vars) => string;
  /**
   * Uebersetzt einen zur Laufzeit gebildeten Schluessel, etwa aus einem Code
   * der Datenbank. `fallback` ist der Wert, der ohne Beschriftung angezeigt
   * wuerde — so bleibt die Oberflaeche auch bei einem unbekannten Code lesbar.
   */
  readonly tCode: (prefix: string, code: string | null | undefined, fallback?: string) => string;
}

const Ctx = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [lang, setLangState] = useState<Language>(readStored);

  // Die Sprache des Dokuments folgt der Auswahl. Ohne das bliebe die
  // Umschaltung rein optisch -- Vorlesewerkzeuge und die Silbentrennung des
  // Browsers richten sich nach diesem Attribut.
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); }
    catch { /* Auswahl gilt dann nur fuer diese Sitzung */ }
  }, []);

  const value = useMemo<I18n>(() => ({
    lang,
    setLang,
    t: (key, vars) => translate(lang, key, vars),
    tCode: (prefix, code, fallback) => {
      if (!code) return fallback ?? "";
      const dict = DICTIONARIES[lang] as Record<string, string | undefined>;
      return dict[`${prefix}.${code}`] ?? fallback ?? code;
    },
  }), [lang, setLang]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n ausserhalb von I18nProvider verwendet.");
  return ctx;
}

/** Kurzform fuer den haeufigsten Fall. */
export function useT(): I18n["t"] {
  return useI18n().t;
}

/**
 * Setzt den Browser-Titel nach dem Muster „Bereich | greenius® homebase".
 * Der Markenname wird nicht uebersetzt.
 */
export function usePageTitle(title: string): void {
  useEffect(() => { document.title = `${title} | greenius® homebase`; }, [title]);
}
